import { z } from 'zod';
import * as Sentry from '@sentry/react';

class SchemaMismatchError extends Error {
  override name = 'SchemaMismatchError';
}

const MAX_REPORTED_ISSUES = 20;

// The dedupe key: strips the query string and collapses id-like path
// segments (numeric, 0x-hex, uuid) to ':id'. Doubles as the Sentry
// FINGERPRINT — one Sentry issue per source+endpoint shape, not one per
// user/market id.
export function schemaMismatchKey(source: string, endpoint: string): string {
  const [path = endpoint] = endpoint.split('?');
  const normalized = path
    .split('/')
    .map((seg) =>
      (
        /^(\d+|0x[0-9a-fA-F]+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(
          seg,
        )
      ) ?
        ':id'
      : seg,
    )
    .join('/');
  return `${source}:${normalized}`;
}

// Dedupe: once per key per pageview — module-scope Set shared by every
// importer; diagnostic state, deliberately never reset.
const reported = new Set<string>();

// Exported separately from the default entry so the Sentry semantics are
// testable in isolation.
// Context carries the first 20 issues as "path: [code] message" strings
// plus the total issueCount, and deliberately NO response body (size/PII —
// the body is recoverable by hitting the endpoint).
export function sendSchemaMismatch(
  source: string,
  endpoint: string,
  error: z.ZodError,
): void {
  const key = schemaMismatchKey(source, endpoint);
  if (reported.has(key)) return;
  reported.add(key);
  Sentry.withScope((scope) => {
    scope.setTag('source', 'schemaMismatch');
    scope.setFingerprint([key]);
    scope.setContext('schema', {
      source,
      endpoint,
      issueCount: error.issues.length,
      issues: error.issues
        .slice(0, MAX_REPORTED_ISSUES)
        .map(
          (i) => `${i.path.map(String).join('.')}: [${i.code}] ${i.message}`,
        ),
    });
    Sentry.captureException(new SchemaMismatchError(`${key} schema mismatch`));
  });
}

// One shared reporter for zod schema drift at API parse boundaries — so
// every boundary reports identically and drift is visible without ever
// blocking the UI (callers fall back to the raw payload themselves; this
// only reports). On localhost (the VITE_IS_LOCALHOST define) it
// console.errors the zod error tree plus the raw body and stops; everywhere
// else it captures a SchemaMismatchError to Sentry. `source` namespaces the
// key/fingerprint: a closed union so a new parse boundary is a deliberate
// vocabulary addition, never a typo'd new Sentry-issue namespace.
export function reportSchemaMismatch(
  source:
    | 'fetchApi'
    | 'v2'
    | 'scoreApi'
    | 'arenaApi'
    | 'strategiesApi'
    | 'assistantApi'
    | 'personalAgentApi'
    | 'analysesApi',
  endpoint: string,
  error: z.ZodError,
  raw: unknown, // logged on localhost only — never sent to Sentry
): void {
  if (import.meta.env.VITE_IS_LOCALHOST) {
    // eslint-disable-next-line no-console -- localhost dev tripwire
    console.error(
      `[${source}:${endpoint}] schema mismatch`,
      z.treeifyError(error),
      { raw },
    );
    return;
  }
  sendSchemaMismatch(source, endpoint, error);
}
